import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const MAX_BATCH = 50;
const FLUSH_INTERVAL_MS = 30_000;

// Module-level event queue — persists across renders, flushed on route change or interval.
let _queue = [];
let _username = '';
let _pageEnterTime = Date.now();
let _prevPath = '';
let _viewEnded = false;

function _gtag(...args) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
}

async function _flush({ keepalive = false } = {}) {
  if (_queue.length === 0) return;
  const batch = _queue.splice(0, MAX_BATCH);
  try {
    await fetch(`${API_BASE_URL}/audit/events`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
      ...(keepalive ? { keepalive: true } : {}),
    });
  } catch (_) {
    // Non-fatal — analytics loss is acceptable
  }
}

function _enqueue(event) {
  _queue.push(event);
  if (_queue.length >= MAX_BATCH) _flush();
}

// One pageview row per view, written when the view ends (page_event has no update path).
function _endView() {
  if (!_prevPath || _viewEnded) return;
  _viewEnded = true;
  _enqueue({
    event_type: 'pageview',
    path: _prevPath,
    duration_ms: Date.now() - _pageEnterTime,
  });
}

function _onLeave() {
  _endView();
  _flush({ keepalive: true });
}

function _onVisibility() {
  if (document.visibilityState === 'hidden') {
    _onLeave();
  } else if (_viewEnded && _prevPath) {
    _pageEnterTime = Date.now();
    _viewEnded = false;
  }
}

export function trackPageView(path, username) {
  if (username) _username = username;

  _endView();

  _prevPath = path;
  _pageEnterTime = Date.now();
  _viewEnded = false;

  // GA4 page_view (SPA manual fire)
  _gtag('event', 'page_view', {
    page_path: path,
    page_title: document.title,
  });
}

export function trackFeature(featureKey, metadata) {
  _enqueue({
    event_type: 'feature',
    path: window.location.pathname,
    feature_key: featureKey,
    metadata: metadata || null,
    duration_ms: null,
  });

  _gtag('event', featureKey, { ...metadata });
}

export function usePageTracking(username) {
  const location = useLocation();
  const intervalRef = useRef(null);

  // Set GA4 user_id once username is known
  useEffect(() => {
    if (username) {
      _username = username;
      _gtag('set', { user_id: username });
    }
  }, [username]);

  // Track route changes
  useEffect(() => {
    trackPageView(location.pathname, username);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flush on interval
  useEffect(() => {
    intervalRef.current = setInterval(_flush, FLUSH_INTERVAL_MS);
    return () => {
      clearInterval(intervalRef.current);
      _flush(); // flush on unmount
    };
  }, []);

  // End the current view and flush when the page is hidden or unloaded
  useEffect(() => {
    window.addEventListener('pagehide', _onLeave);
    window.addEventListener('beforeunload', _onLeave);
    document.addEventListener('visibilitychange', _onVisibility);
    return () => {
      window.removeEventListener('pagehide', _onLeave);
      window.removeEventListener('beforeunload', _onLeave);
      document.removeEventListener('visibilitychange', _onVisibility);
    };
  }, []);

  return { trackFeature };
}

export default usePageTracking;
